// A dashboard that already says something, for one experiment.
//
// **Why a blank canvas is the wrong default.** Everything the canvas needs to
// open a useful board is already known the moment somebody picks an experiment:
// which sources carry rows for it, which column names it there, and which five
// tiles a person building an experiment readout builds every single time. A
// blank grid makes them rebuild those five by hand, and the first question
// becomes "which of these six views has the arm rates" rather than "did the arm
// move the number".
//
// **Every recipe is SKIPPED rather than approximated.** A recipe names the
// source it reads and every column it touches, and `starterTiles` drops it whole
// if this environment's warehouse does not declare all of them. That is what
// makes the seed safe across the three environments and across a warehouse that
// keeps moving: a renamed column costs one tile, not a board of charts that 422
// on save. It is also why the recipes name columns explicitly instead of
// guessing by role — a guess produces a chart that renders and means nothing.
//
// The tiles are ordinary built charts. Nothing here is privileged: every one of
// them is editable, deletable and duplicable the moment it lands, and each is
// built through `defaultChartForKind` so its `display` object is exactly the
// one the canvas would have produced for that tile kind.
import type {
  BiSource,
  ChartSpec,
  NativeFilterSpec,
  SimpleAggregate,
  SimpleMetric,
} from "@bi/shared/types";

import { newUuid } from "./new-uuid";
import {
  type StudioDoc,
  type TileKind,
  addChart,
  addControl,
  addMarkdown,
  defaultChartForKind,
  newControl,
  newStudioDoc,
  putParameter,
} from "./studio-model";

/** The parameter name a seeded dashboard binds its experiment control to.
 *  The same name `/x/{key}` seeds, so a dashboard built here and one promoted
 *  to an authored template take a URL value the same way. */
export const EXPERIMENT_PARAMETER = "experiment";

interface StarterMeasure {
  column: string | null;
  aggregate: SimpleAggregate;
  label: string;
}

/** One tile the seed will lay down, if the warehouse can carry it. */
export interface StarterRecipe {
  key: string;
  sourceId: string;
  kind: TileKind;
  title: string;
  /** The sentence the tile carries. Not decoration: three of these five tiles
   *  are numbers whose obvious reading is wrong (a mean of daily rates, a mean
   *  of daily deltas, a cumulative rung), and the caveat is what stops the tile
   *  teaching the wrong thing on its first render. */
  description: string;
  groupby: string[];
  measures: StarterMeasure[];
  /** A time column to bucket on, for the one tile that is a series. */
  xAxis?: string;
  sort?: { column: string; ascending: boolean };
  rowLimit: number;
  width: number;
}

/**
 * The five tiles, in the order they are laid down.
 *
 * Read as data rather than as code on purpose: adding a sixth is a row here,
 * and it says which source it needs and which columns it touches, which is
 * exactly what `starterTiles` checks before laying it down.
 */
export const STARTER_RECIPES: readonly StarterRecipe[] = [
  {
    key: "kpi-ladder",
    sourceId: "studio_experiment_kpi_daily",
    kind: "table",
    title: "KPI ladder by arm",
    description:
      "Every measurement this experiment has, per arm. Read the horizon first: a weeks-KPI on day two reads as no effect and is not. The rate is the numerator over the denominator, summed across the window and divided once; averaging the per-day rates instead is Simpson's paradox.",
    groupby: ["horizon_order", "kpi_label", "arm"],
    measures: [
      { column: "num", aggregate: "sum", label: "Numerator" },
      { column: "den", aggregate: "sum", label: "Denominator" },
      { column: "unit_n", aggregate: "sum", label: "Units" },
    ],
    sort: { column: "horizon_order", ascending: true },
    rowLimit: 400,
    width: 12,
  },
  {
    key: "exposures",
    sourceId: "studio_project_arms",
    kind: "table",
    title: "Exposures per arm",
    description:
      "How many projects each arm actually holds. The first thing to check after the sample-ratio verdict: an arm that never filled is a wiring problem, not a result.",
    groupby: ["arm"],
    measures: [{ column: "project_id", aggregate: "count_distinct", label: "Projects" }],
    sort: { column: "Projects", ascending: false },
    rowLimit: 50,
    width: 6,
  },
  {
    key: "delta",
    sourceId: "studio_experiment_kpi_daily",
    kind: "table",
    title: "Delta versus the baseline",
    description:
      "Each arm's daily delta against the default arm's all-time rate, averaged over the window. Correct as a mean of daily differences, which is a slightly different question from the overall delta. The default arm's own rows are blank because an arm is not compared with itself.",
    groupby: ["kpi_label", "arm"],
    measures: [{ column: "delta_relative", aggregate: "avg", label: "Average daily delta" }],
    rowLimit: 400,
    width: 6,
  },
  {
    key: "funnel",
    sourceId: "studio_funnel_by_arm",
    kind: "table",
    title: "Funnel by arm",
    description:
      "The eight project rungs per arm. Reached is CUMULATIVE, so it falls monotonically down the ladder; stalled is mutually exclusive, so summing it over the stages gives the arm's whole cohort back.",
    groupby: ["stage_order", "stage", "arm"],
    measures: [
      { column: "projects_reached", aggregate: "sum", label: "Reached" },
      { column: "projects_stalled", aggregate: "sum", label: "Stalled" },
    ],
    sort: { column: "stage_order", ascending: true },
    rowLimit: 200,
    width: 12,
  },
  {
    key: "timeline",
    sourceId: "studio_experiment_events",
    kind: "pivot",
    title: "What changed, by day",
    description:
      "Every dial move, allocation version, lifecycle transition, guardrail trip and prompt-changing deploy on this experiment. A day with a tall bar is a day the readout above is measuring a moving target.",
    groupby: ["kind"],
    measures: [{ column: null, aggregate: "count", label: "Changes" }],
    xAxis: "event_date",
    rowLimit: 500,
    width: 12,
  },
];

function declares(source: BiSource, names: readonly (string | null)[]): boolean {
  const columns = new Set((source.columns ?? []).map((c) => c.name));
  return names.every((name) => name === null || columns.has(name));
}

/** Whether this environment's warehouse can carry `recipe`. */
export function recipeIsBuildable(
  recipe: StarterRecipe,
  sourcesById: Readonly<Record<string, BiSource>>,
): boolean {
  const source = sourcesById[recipe.sourceId];
  if (!source) return false;
  const needed = [
    ...recipe.groupby,
    ...recipe.measures.map((m) => m.column),
    ...(recipe.xAxis ? [recipe.xAxis] : []),
    // A sort naming a MEASURE label is legal and is not a column, so it is
    // checked against the labels first — the same rule the backend's
    // `_is_metric_label` applies before it reports an unknown column.
    ...(recipe.sort && !recipe.measures.some((m) => m.label === recipe.sort?.column)
      ? [recipe.sort.column]
      : []),
  ];
  return declares(source, needed);
}

/** One recipe as a built `ChartSpec`, or `null` when the warehouse cannot carry
 *  it. Built on top of `defaultChartForKind` so the tile's `display` object is
 *  the canvas's own, never a second hand-written copy that drifts from it. */
export function starterChart(
  recipe: StarterRecipe,
  sourcesById: Readonly<Record<string, BiSource>>,
  datasetUuidBySource: Readonly<Record<string, string>>,
): ChartSpec | null {
  const source = sourcesById[recipe.sourceId];
  const datasetUuid = datasetUuidBySource[recipe.sourceId];
  if (!source || !datasetUuid || !recipeIsBuildable(recipe, sourcesById)) return null;

  const base = defaultChartForKind(recipe.kind, source, datasetUuid, {
    uuid: newUuid(),
    title: recipe.title,
  });
  const metrics: SimpleMetric[] = recipe.measures.map((m) => ({
    kind: "simple",
    aggregate: m.aggregate,
    column: m.column,
    label: m.label,
  }));
  return {
    ...base,
    description: recipe.description,
    query: {
      ...base.query,
      metric: null,
      metrics,
      groupby: recipe.groupby,
      x_axis: recipe.xAxis ?? null,
      order_by: recipe.sort
        ? [{ column: recipe.sort.column, ascending: recipe.sort.ascending }]
        : [],
      row_limit: recipe.rowLimit,
    },
  };
}

export interface StarterSeed {
  doc: StudioDoc;
  /** The recipes this environment could not carry, by key. Reported rather than
   *  swallowed: a board that is two tiles short should say which two and why,
   *  or somebody concludes the experiment has no funnel. */
  skipped: string[];
  /** The control the experiment is bound through, so the caller can seed the
   *  live filter state from it without re-deriving which control it was. */
  controlId: string | null;
}

/**
 * A whole starting dashboard for one experiment.
 *
 * `coverage` is the register's answer for this key: which sources carry rows
 * for it and, per source, the column that names it there. It decides two
 * things — which recipes are worth laying down at all (a source with nothing
 * for this experiment would draw a blank a reader would take as a finding), and
 * which column the experiment control binds to, which is NOT always
 * `experiment`.
 *
 * The control is created with a `DashboardParameter` on it, which is what makes
 * the result a TEMPLATE rather than a one-off: the same board reopens against
 * another experiment by rebinding that parameter, with no copy and no edit.
 */
export function starterDoc(
  experimentKey: string,
  coverage: readonly { sourceId: string; column: string }[],
  sourcesById: Readonly<Record<string, BiSource>>,
  datasetUuidBySource: Readonly<Record<string, string>>,
): StarterSeed {
  const columnBySource = new Map(coverage.map((c) => [c.sourceId, c.column]));
  let doc = newStudioDoc(`${experimentKey}: experiment readout`);

  // The control first, so every tile laid down after it is inside its scope
  // from the first render rather than after somebody notices and adds one.
  const primary = coverage[0] ?? null;
  let controlId: string | null = null;
  if (primary) {
    const control: NativeFilterSpec = newControl("select", {
      name: "Experiment",
      target_column: primary.column,
      target_dataset_uuid: datasetUuidBySource[primary.sourceId] ?? null,
      options_from: { source_id: primary.sourceId, column: primary.column },
      default_value: [experimentKey],
      multi_select: false,
    });
    controlId = control.id;
    doc = addControl(doc, control);
    doc = putParameter(doc, {
      name: EXPERIMENT_PARAMETER,
      filter_id: control.id,
      default: experimentKey,
    });
  }

  doc = addMarkdown(
    doc,
    `## ${experimentKey}\n\nRead the sample before the rates, and the horizon before any cell: a measurement whose horizon has not passed reads as no effect and is not. Every tile here is an ordinary chart, so change it, delete it, or add your own.`,
  );

  const skipped: string[] = [];
  for (const recipe of STARTER_RECIPES) {
    // A source with no rows for THIS experiment is skipped even when the
    // warehouse declares it: an empty tile under a specific experiment's name
    // is read as a result, and it is not one.
    const covered = coverage.length === 0 || columnBySource.has(recipe.sourceId);
    const chart = covered ? starterChart(recipe, sourcesById, datasetUuidBySource) : null;
    if (!chart) {
      skipped.push(recipe.key);
      continue;
    }
    doc = addChart(doc, chart, recipe.width);
  }
  return { doc, skipped, controlId };
}
